/**
 * useKeyboardInset — PROD-1194. Keeps the pinned primary button (BottomActionBar) above the
 * on-screen keyboard on the WEB.
 *
 * While at least one BottomActionBar is mounted, this listens to window.visualViewport and
 * writes the height the keyboard covers to `--kb` on <html> (and `data-kb-open` while it is
 * open). index.css then sticks `.bottom-action-bar` at `bottom: var(--kb)` (above the keyboard,
 * no home-indicator padding) and reserves `--kb` of scroll space at the end of .scroll-wrapper.
 * If the focused field would sit under the lifted bar, the one scroller (.scroll-wrapper) is moved
 * by the overlap; the reserve is what gives that scroll room on a short page or at the end of a
 * form. It runs when the keyboard height changes and on every focusin while it is open.
 *
 * ★ Not in the iOS shell: Capacitor resizes the web view for the keyboard, so the layout
 *   viewport already ends at the keyboard and a lift would be paid twice. Not without
 *   visualViewport (old browsers): nothing is written and the bar stays at the bottom.
 * The arithmetic is utils/keyboardInset.js; scripts/test-keyboard-inset.mjs holds it, the sticky +
 * reserve layout read from index.css, and this hook's shell guard, listeners and cleanup in node;
 * check-browser-render R27 measures the field above the bar on /verify and /signup.
 */
import { useEffect } from 'react'
import { Capacitor } from '@capacitor/core'
import { keyboardInset, fieldOverlap } from '../utils/keyboardInset'

let users = 0
let detach = null

function attach() {
  const vv = typeof window !== 'undefined' ? window.visualViewport : null
  if (!vv || Capacitor.isNativePlatform()) return null
  const root = document.documentElement
  let last = -1
  let frame = 0
  // the focused field must end above the lifted bar; index.css reserves --kb of scroll space at the
  // end of .scroll-wrapper while the keyboard is open, so this scroll has room even on a short page
  // or at the end of a form (PROD-1194 review H1)
  const clearField = () => {
    cancelAnimationFrame(frame)
    frame = requestAnimationFrame(() => {
      const field = document.activeElement
      if (!field || !/^(INPUT|TEXTAREA|SELECT)$/.test(field.tagName)) return
      const bar = field.closest('form, [data-bottom-action-scope]')?.querySelector('[data-bottom-action-bar]')
        || document.querySelector('[data-bottom-action-bar]')
      if (!bar || bar.contains(field)) return
      // measure against where the lifted bar will SIT, not where it is now: a sticky bar is held
      // inside its parent (a form that starts low caps `bottom: var(--kb)` at the form's top), and it
      // jumps to the keyboard once the page has scrolled, so the current rect alone scrolls too little
      // (PROD-1194 re-review H1-b)
      const barRect = bar.getBoundingClientRect()
      const target = vv.offsetTop + vv.height - bar.offsetHeight
      const over = fieldOverlap(field.getBoundingClientRect(), { top: Math.min(barRect.top, target) })
      if (over > 0) document.querySelector('.scroll-wrapper')?.scrollBy({ top: over })
    })
  }
  const update = () => {
    const kb = keyboardInset({ innerHeight: window.innerHeight, vvHeight: vv.height, vvOffsetTop: vv.offsetTop })
    if (kb === last) return
    last = kb
    root.style.setProperty('--kb', `${kb}px`)
    if (kb > 0) root.setAttribute('data-kb-open', '')
    else root.removeAttribute('data-kb-open')
    if (kb > 0) clearField()
  }
  // focus moved to another field while the keyboard stays the same height (iOS "next" arrow, a tap
  // on a lower field): no viewport event comes, so the field step runs on focusin (review M1)
  const onFocusIn = () => { if (last > 0) clearField() }
  // back from the background (or the back-forward cache) the keyboard may be gone without a viewport
  // event: re-read, or the bar floats --kb high and the page ends in white (re-review L-a)
  const onShow = () => { if (document.visibilityState !== 'hidden') update() }
  vv.addEventListener('resize', update)
  vv.addEventListener('scroll', update)
  document.addEventListener('focusin', onFocusIn)
  document.addEventListener('visibilitychange', onShow)
  window.addEventListener('pageshow', onShow)
  update()
  return () => {
    cancelAnimationFrame(frame)
    vv.removeEventListener('resize', update)
    vv.removeEventListener('scroll', update)
    document.removeEventListener('focusin', onFocusIn)
    document.removeEventListener('visibilitychange', onShow)
    window.removeEventListener('pageshow', onShow)
    root.style.removeProperty('--kb')
    root.removeAttribute('data-kb-open')
  }
}

export default function useKeyboardInset() {
  useEffect(() => {
    if (users++ === 0) detach = attach()
    return () => {
      if (--users === 0 && detach) { detach(); detach = null }
    }
  }, [])
}
